import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import styles from './VideoCall.module.css';
import { useDocumentHead } from '../../hooks/useDocumentHead';
import { authenticatedFetch } from '../../utils/api';
import ReviewModal from '../ReviewModal/ReviewModal';

const VideoCall = ({ roomUrl, participantName, onClose }) => {
    useDocumentHead(
        'Video Coaching Session | TopSpeaks',
        'Join live video coaching sessions with speaking experts. Get real-time feedback and personalized guidance to improve your speaking skills.',
        'index, follow'
    );
    const callFrameRef = useRef(null);
    const containerRef = useRef(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [coachId, setCoachId] = useState(null);
    const [bookingId, setBookingId] = useState(null);
    const [isUser, setIsUser] = useState(false);
    // eslint-disable-next-line
    const [connectionState, setConnectionState] = useState('initializing');
    // eslint-disable-next-line
    const [sessionHistory, setSessionHistory] = useState({
        joins: [],
        leaves: []
    });
    const hasLeftRef = useRef(false); // Track if leave event has been handled

    const trackSessionEvent = async (eventType, bookingId) => {
        // For leave events, check if we've already tracked this leave
        if (eventType === 'leave' && hasLeftRef.current) {
            return;
        }

        try {
            const response = await authenticatedFetch(`/api/video/track/${eventType}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ bookingId })
            });

            if (!response.ok) {
                console.error(`Failed to track ${eventType} event:`, await response.text());
                return;
            }

            // Update local session history
            const timestamp = new Date().toISOString();
            setSessionHistory(prev => ({
                ...prev,
                [eventType === 'join' ? 'joins' : 'leaves']: [...prev[eventType === 'join' ? 'joins' : 'leaves'], timestamp]
            }));

            // If this was a leave event, mark that we've handled it
            if (eventType === 'leave') {
                hasLeftRef.current = true;
            }

            return response;
        } catch (error) {
            console.error(`Failed to track ${eventType}:`, error);
            throw error;
        }
    };

    useEffect(() => {
        // Extract bookingId, coachId, and role from roomUrl
        const urlParams = new URLSearchParams(roomUrl.split('?')[1]);
        setBookingId(urlParams.get('bookingId'));
        setCoachId(roomUrl.split('/')[3].split('?')[0]);
        // Check if the participant is a user (not a coach) by checking the role parameter
        setIsUser(urlParams.get('role') === 'user');
    }, [roomUrl]);

    useEffect(() => {
        let scriptElement = null;
        let isMounted = true;

        const initializeCall = async () => {
            if (!isMounted) return;

            try {
                if (callFrameRef.current) {
                    callFrameRef.current.destroy();
                    callFrameRef.current = null;
                }

                scriptElement = document.createElement('script');
                scriptElement.src = 'https://unpkg.com/@daily-co/daily-js';
                scriptElement.crossOrigin = 'anonymous';

                await new Promise((resolve, reject) => {
                    scriptElement.onload = resolve;
                    scriptElement.onerror = () => reject(new Error('Failed to load Daily.co script'));
                    document.body.appendChild(scriptElement);
                });

                await new Promise(resolve => setTimeout(resolve, 100));

                if (!containerRef.current || !isMounted) return;

                const callFrame = window.Daily.createFrame(containerRef.current, {
                    showLeaveButton: true,
                    showFullscreenButton: true,
                    userName: participantName,
                    iframeStyle: {
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: '8px',
                    },
                });

                if (!isMounted) {
                    callFrame.destroy();
                    return;
                }

                callFrameRef.current = callFrame;

                setupEventListeners(callFrame);

                try {
                    await callFrame.join({ url: roomUrl });

                    if (!isMounted) return;
                    setIsLoading(false);
                    setConnectionState('connected');
                } catch (joinError) {
                    if (!isMounted) return;
                    setError(`Failed to join video call. Please try again.`);
                    setIsLoading(false);
                    setConnectionState('failed');
                }

            } catch (err) {
                if (!isMounted) return;
                setError(`Failed to initialize video call. Please try again.`);
                setIsLoading(false);
                setConnectionState('failed');
            }
        };

        const setupEventListeners = (callFrame) => {
            callFrame.on('joining-meeting', () => {
                if (!isMounted) return;
                setConnectionState('joining');
            });

            callFrame.on('joined-meeting', async () => {
                if (!isMounted) return;
                setConnectionState('connected');
                setIsLoading(false);

                try {
                    const bookingId = new URLSearchParams(roomUrl.split('?')[1]).get('bookingId');
                    if (bookingId) {
                        await trackSessionEvent('join', bookingId);
                    }
                } catch (error) {
                    console.error('Failed to track join:', error);
                }
            });

            callFrame.on('left-meeting', async () => {
                if (!isMounted) return;

                try {
                    const bookingId = new URLSearchParams(roomUrl.split('?')[1]).get('bookingId');
                    if (bookingId) {
                        await trackSessionEvent('leave', bookingId);
                    }
                } catch (error) {
                    console.error('Failed to track leave:', error);
                }
            });

            callFrame.on('error', (err) => {
                if (!isMounted) return;
                setError(`Video call error. Please try again.`);
                setConnectionState('error');
            });

            callFrame.on('network-quality-change', (event) => {
                if (!isMounted) return;
                console.log('Network quality changed:', event);
            });

            callFrame.on('participant-joined', (event) => {
                if (!isMounted) return;
                console.log('Participant joined:', event);
            });

            callFrame.on('participant-left', (event) => {
                if (!isMounted) return;
                console.log('Participant left:', event);
            });
        };

        initializeCall();

        return () => {
            isMounted = false;
            if (callFrameRef.current) {
                const bookingId = new URLSearchParams(roomUrl.split('?')[1]).get('bookingId');
                if (bookingId && !hasLeftRef.current) { // Only track leave if we haven't already
                    trackSessionEvent('leave', bookingId).catch(console.error);
                }
                callFrameRef.current.destroy();
                callFrameRef.current = null;
            }
            if (scriptElement && document.body.contains(scriptElement)) {
                document.body.removeChild(scriptElement);
            }
        };
    }, [roomUrl, participantName]);

    const handleCloseClick = async () => {
        if (callFrameRef.current) {
            await callFrameRef.current.leave();
            // Only show review modal if the participant is a user
            if (isUser) {
                setShowReviewModal(true);
            } else {
                onClose(); // For coaches, just close the video call
            }
        }
    };

    const handleFinalClose = () => {
        setShowReviewModal(false);
        onClose();
    };


    return (
        <>
            <div className={styles.videoCallContainer}>
                <div className={styles.header}>
                    <h2>Video Session</h2>
                    <button onClick={handleCloseClick} className={styles.closeButton}>
                        <X size={20} />
                    </button>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {isLoading && (
                    <div className={styles.loading}>
                        Connecting to video call...
                    </div>
                )}

                <div
                    ref={containerRef}
                    className={styles.videoFrame}
                />
            </div>
            {showReviewModal && isUser && (
                <ReviewModal
                    coachId={coachId}
                    bookingId={bookingId}
                    onClose={handleFinalClose}
                />
            )}
        </>
    );
};

export default VideoCall;